import { useCallback } from 'react';
import { SnackbarMessage } from 'notistack';
import { ScheduleService } from 'services/ScheduleService';
import { SchedulingRuleBase } from 'repository/schedule/@types/SchedulingRuleModel';

interface AlertMessage {
    title: string;
    message: SnackbarMessage;
    severity: 'error' | 'success' | 'warning' | 'info';
}

type SetLoading = (loading: boolean) => void;
type Callback = () => void;
type HandlerAlert = (message: AlertMessage) => void;

const scheduleService = new ScheduleService();

export const useSchedulingActions = () => {
    const addSchedulingRule = useCallback(
        async ({
            setLoading,
            body,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            body: SchedulingRuleBase;
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);

            try {
                console.log('body', body);
                await scheduleService.scheduling_rules.create(body);
                reloading();
                closing();
                handlerAlert(messageSuccess);
            } catch (error: any) {
                console.error(error?.message || error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const updateSchedulingRule = useCallback(
        async ({
            setLoading,
            schedulingRuleId,
            body,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            schedulingRuleId: number;
            body: SchedulingRuleBase;
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);

            try {
                await scheduleService.scheduling_rules.update(schedulingRuleId, body);
                reloading();
                closing();
                handlerAlert(messageSuccess);
            } catch (error: any) {
                console.error(error?.message || error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const deleteSchedulingRules = useCallback(
        async ({
            setLoading,
            schedulingRuleIds,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            schedulingRuleIds: number[];
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);

            try {
                await Promise.all(schedulingRuleIds.map((schedulingRuleId) => scheduleService.scheduling_rules.delete(schedulingRuleId)));
                reloading();
                closing();
                handlerAlert(messageSuccess);
            } catch (error: any) {
                console.error(error?.message || error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    return {
        addSchedulingRule,
        updateSchedulingRule,
        deleteSchedulingRules,
    };
};
